import { Injectable } from '@angular/core';
import { Http, RequestOptions, Response } from '@angular/http';
import { UtilService } from '../util';
import { Patient } from '../../models';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import 'rxjs/add/observable/of';

import { PatientReg } from 'app/shared/models/patient-reg.model';
import { FeatureFlagService } from '../feature-flag';
import { Report } from 'app/shared/models/report.model';

/**
 * This class provides the Patient service with methods to read patients
 */
@Injectable()
export class PatientService {
  constructor(
    private http: Http,
    private utilService: UtilService,
    private featureFlagService: FeatureFlagService
  ) {}

  /**
   * Method to retreive all patients within the given searchCriteria.
   * @param searchCriteria - search, filter, orderBy
   * @returns {Observable<R|T>}
   */
  getPatients(searchCriteria: SearchCriteria): Observable<Array<Patient>> {
    let searchUrl: string = '/core/patients';
    let queryParams = this.utilService.setQueryParams({
      sort: searchCriteria.Sort,
      order: searchCriteria.Order,
      q: searchCriteria.SearchTerm,
      page: searchCriteria.PageNumber,
    });
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(searchUrl, options)
      .map((res: Response) => {
        return (this.utilService.outputParser(res) || []).map((patient) => {
          return new Patient(patient);
        });
      })
      .catch(this.utilService.handleError);
  }

  getPatientsUpdated(searchCriteria: any): Observable<Array<Patient>> {
    let searchUrl: string = '/core/patients';
    let queryParams = this.utilService.setQueryParams(searchCriteria);
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(searchUrl, options)
      .map((res: Response) => {
        return (this.utilService.outputParser(res) || []).map((patient) => {
          return patient;
        });
      })
      .catch(this.utilService.handleError);
  }

  getPatientsHeaderProp(searchCriteria: SearchCriteria) {
    let searchUrl: string = '/core/patients';
    let queryParams = this.utilService.setQueryParams({
      sort: searchCriteria.Sort,
      order: searchCriteria.Order,
      q: searchCriteria.SearchTerm,
      page: searchCriteria.PageNumber,
    });
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(searchUrl, options)
      .map((res: Response) => {
        return res;
      })
      .catch(this.utilService.handleError);
  }

  getDuplicatePatients(lastName, firstName, dateOfBirth) {
    let dob = `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`;

    return this.http
      .get(`/core/patients?last_name=${lastName}&first_name=${firstName}&date_of_birth=${dob}`)
      .map((res: Response) => {
        return res;
      });
  }

  getPatientsByLocation(
    locationId: number | string,
    searchCriteria: SearchCriteria
  ): Observable<Array<Patient>> {
    let searchUrl: string = '/location/' + locationId + '/patients';
    let queryParams = this.utilService.setQueryParams({
      sort: searchCriteria.Sort,
      order: searchCriteria.Order,
      search: searchCriteria.SearchTerm,
      page: searchCriteria.PageNumber,
    });
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(searchUrl, options)
      .map((res: Response) => {
        return (this.utilService.outputParser(res) || {}).Patients || [];
      })
      .catch(this.utilService.handleError);
  }

  getPatientById(patientId: number | string): Observable<Patient> {
    // TODO: PP20-57 - server needs to return Dob with same format as /patients
    // Double check that ticket and ensure validity
    let findUrl: string = '/core/patients/' + patientId;
    return this.http
      .get(findUrl)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  getPatientOrder(patientId: string, orderId: string): Observable<Order> {
    const url = `/encounters/${orderId}`;
    return this.http
      .get(url)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || [];
      })
      .catch(this.utilService.handleError);
  }

  getReportsByPatient(
    patientId: number | string,
    searchCriteria: SearchCriteria = null,
    facilityId?: string
  ): Observable<Report[]> {
    let findUrl: string;

    if (this.featureFlagService.hide_extra_reports) {
      findUrl =
        '/core/patients/' +
        patientId +
        `/reports?facility_id=${facilityId}&tag_list=final_report,patient_final_report`;
    } else {
      findUrl = '/core/patients/' + patientId + `/reports?facility_id=${facilityId}`;
    }

    let queryObject: any = searchCriteria
      ? {
          sort: searchCriteria.Sort,
          order: searchCriteria.Order,
          q: searchCriteria.SearchTerm,
          page: searchCriteria.PageNumber,
        }
      : {};
    let queryParams = this.utilService.setQueryParams(queryObject);
    let options = new RequestOptions({ params: queryParams });
    return this.http
      .get(findUrl, options)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || [];
      })
      .catch(this.utilService.handleError);
  }

  getEncounterOrder(orderId: string): Observable<Order> {
    let url: string;

    if (this.featureFlagService.hide_extra_reports) {
      url = `/chart/encounters/${orderId}?tag_list[]=final_report&tag_list[]=patient_final_report`;
    } else {
      url = `/chart/encounters/${orderId}`;
    }

    return this.http
      .get(url)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || [];
      })
      .catch(this.utilService.handleError);
  }

  getReports(
    searchCriteria: SearchCriteria,
    physicianId?: number,
    locationId?: number | string
  ): Observable<Order[]> {
    let findUrl: string = '/chart/encounters/reported';
    let queryObject: any = {
      sort: searchCriteria.Sort,
      order: searchCriteria.Order,
      q: searchCriteria.SearchTerm,
      page: searchCriteria.PageNumber,
    };
    if (Boolean(locationId)) {
      queryObject.locationId = locationId;
    } else if (Boolean(physicianId)) {
      queryObject.physicianId = physicianId;
    }
    let queryParams = this.utilService.setQueryParams(queryObject);
    let options = new RequestOptions({ params: queryParams });
    return this.http
      .get(findUrl, options)
      .map((res: Response) => {
        const reportSize = res.headers.get('total');
        return { main: this.utilService.outputParser(res), size: reportSize } || [];
      })
      .catch(this.utilService.handleError);
  }

  getTestOrders(
    searchCriteria: SearchCriteria,
    physicianId?: number,
    locationId?: number | string,
    facilityId?: string
  ): Observable<Order[]> {
    const url: string = `/chart/encounters`;
    let queryObject: any = {
      q: searchCriteria.SearchTerm,
      page: searchCriteria.PageNumber,
      include_associations: true,
      start_date: searchCriteria.startDate,
      end_date: searchCriteria.endDate,
      facility_id: facilityId
    };
  
    if (searchCriteria.SearchTerm) {
      const checkAccession = searchCriteria.SearchTerm.trim().slice(0, 4).toLowerCase();
      if (checkAccession === 'com-') {
        delete queryObject.q;
        queryObject['accession_number'] = searchCriteria.SearchTerm;
      }
    };
    
    if (Boolean(physicianId)) {
      queryObject.locationId = physicianId;
    } else if (Boolean(locationId)) {
      queryObject.locationId = locationId;
    }
    let queryParams = this.utilService.setQueryParams(queryObject);
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(url, options)
      .map((res: Response) => {
        const reportSize = res.headers.get('total');
        return { main: this.utilService.outputParser(res), size: reportSize } || [];
      })
      .catch(this.utilService.handleError);
  }

  exists(patient: Patient): Observable<boolean> {
    let existsUrl: string = '/patients/exists';
    let queryParams = this.utilService.setQueryParams({
      email: patient.Email,
      id: patient.Id,
    });
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(existsUrl, options)
      .map((res: Response) => {
        return (this.utilService.outputParser(res) || {}).Exists || false;
      })
      .catch(this.utilService.handleError);
  }

  createPatient(patient: Patient): Observable<Patient> {
    let url: string = '/core/patients';

    return this.http
      .post(url, patient)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  sendActivationEmail(patient: Patient) {
    let url: string = '/patients/' + patient.Id + '/activate';

    return this.http
      .post(url, patient)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  completeRegistration(patientReg: PatientReg): Observable<any> {
    let url: string = `/users/${patientReg.UserId}/password`;

    return this.http
      .post(url, patientReg)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  updatePatientProfile(patient: Patient): Observable<Object> {
    let updateUrl: string = '/core/patients/' + patient.Id;
    return this.http
      .put(updateUrl, patient)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  clearPaymentPatientProfile(patient: any, patientId): Observable<Object> {
    let updateUrl: string = '/core/patients/' + patientId;
    return this.http
      .put(updateUrl, patient)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  updatePatientBilling(patient: Patient): Observable<Object> {
    let updateUrl: string =
      '/core/patients/' + patient.Id + '/insurance_policies/' + patient.InsurancePolicies[0].Id;

    const payload = {
      Name: patient.InsurancePolicies[0].Name,
      BillTo: patient.InsurancePolicies[0].BillTo,
      Provider: patient.InsurancePolicies[0].Provider,
      PlanNumber: patient.InsurancePolicies[0].PlanNumber,
      GroupNumber: patient.InsurancePolicies[0].GroupNumber,
      DoctorId: patient.DoctorId,
      // TODO: in future patient's insurance info includes address
      // we should use the API to retrieve it
      Address: {
        Address1: null,
        Address2: null,
        City: null,
        State: null,
        Zip: null,
        County: null,
        Country: null,
      },
    };

    return this.http
      .put(updateUrl, payload)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  updatePatientPhysician(patient: Patient): Observable<Object> {
    let updateUrl: string = '/patients/' + patient.Id + '/physician/' + patient.DoctorId;
    return this.http
      .put(updateUrl, {})
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  getLatestTestResults(patientId: number) {
    let url = '/patients/' + patientId + '/test-results';
    return this.http
      .get(url)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/patient/patient.service.ts